import { arcPath, formatPercent } from './viz';

type Tone = 'neutral' | 'good' | 'warning' | 'critical';

interface ArcMeterProps {
  /** 0–1. Null when the ratio has no denominator — drawn as an empty track, never as 0%. */
  value: number | null | undefined;
  label: string;
  /** Overrides the centred percentage, e.g. a duration or a raw count. */
  display?: string;
  /** A reference line on the track: an industry benchmark, last period, or a target. */
  benchmark?: { value: number; label: string };
  tone?: Tone;
  size?: number;
  /** One line under the number — what the ratio is OF. */
  caption?: string;
}

/** Reserved status tokens. Never a series colour; always paired with the label beside them. */
const TONE_FILL: Record<Tone, string> = {
  neutral: 'var(--series-1)',
  good: 'var(--viz-good)',
  warning: 'var(--viz-warning)',
  critical: 'var(--viz-critical)',
};

const SWEEP = 270;
const START = -135;

/**
 * ONE ratio against its limit.
 *
 * This is the circle that legitimately replaces a pie. A two-slice pie asks the eye to compare
 * two angles that share an edge; a meter puts the value against a full track, so "37%" is read
 * as a position rather than reconstructed from an angle.
 *
 * <b>A null value is not zero.</b> An unmeasured ratio draws an empty track and an em dash — a
 * 0% arc would be indistinguishable from a real zero, and the two mean opposite things.
 *
 * The 270° arc leaves a gap at the bottom so the ends are visibly distinct: a full ring at 99%
 * and at 100% look identical, which is exactly where a meter is read most carefully.
 *
 * <b>Tone is a claim.</b> Passing `critical` says this number is bad, so it ships with its label
 * and never relies on the colour alone — the status palette is reserved for state, and a reader
 * who cannot see the hue still gets the sentence.
 */
export function ArcMeter({
  value, label, display, benchmark, tone = 'neutral', size = 160, caption,
}: ArcMeterProps) {
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 6;
  const rInner = rOuter - Math.max(10, size * 0.075);

  const measured = value !== null && value !== undefined && Number.isFinite(value);
  const clamped = measured ? Math.min(Math.max(value, 0), 1) : 0;
  const fill = TONE_FILL[tone];

  const track = arcPath(cx, cy, rInner, rOuter, 0, SWEEP);
  const filled = clamped > 0 ? arcPath(cx, cy, rInner, rOuter, 0, SWEEP * clamped) : '';

  const markAngle = benchmark ? SWEEP * Math.min(Math.max(benchmark.value, 0), 1) : null;

  return (
    <div className="meter">
      <svg
        width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
        aria-label={`${label}: ${display ?? formatPercent(measured ? clamped : null)}`}
      >
        <g transform={`rotate(${START} ${cx} ${cy})`}>
          <path d={track} className="meter-track" />
          {filled && <path d={filled} fill={fill} className="meter-fill" />}
          {markAngle !== null && (
            <line
              x1={cx} y1={cy - rOuter - 2} x2={cx} y2={cy - rInner + 2}
              className="meter-benchmark"
              transform={`rotate(${markAngle} ${cx} ${cy})`}
            />
          )}
        </g>

        <text x={cx} y={cy + 2} textAnchor="middle" className="meter-hero tabular">
          {display ?? formatPercent(measured ? clamped : null, 1)}
        </text>
        {caption && (
          <text x={cx} y={cy + 20} textAnchor="middle" className="meter-caption">{caption}</text>
        )}
      </svg>

      <div className="meter-label t-label">{label}</div>
      {benchmark && (
        <div className="meter-benchmark-label t-caption subtle">
          {benchmark.label} · {formatPercent(benchmark.value, 0)}
        </div>
      )}
    </div>
  );
}
